import { useEffect } from 'react';
import { IHtmlTagAttribute } from '../models/common/metadata-tagging/html-tag-attribute.model';

type ClientOnlyScriptProps = {
  /** URL of the external script */
  src?: string;
  /** Optional DOM id to prevent duplicates */
  id?: string;
  /** Whether to load asynchronously */
  async?: boolean;
  /** Delay (ms) to wait after hydration before loading */
  delay?: number;
  attributes?: IHtmlTagAttribute[];
  innerHtml?: string;
};

export function ClientOnlyScript({ src, id, async = true, delay = 0, attributes, innerHtml }: ClientOnlyScriptProps) {
  useEffect(() => {
    // skip SSR
    if (typeof window === 'undefined') return;

    // avoid duplicates
    if (id && document.getElementById(id)) return;

    const loadScript = () => {
      const script = document.createElement('script');
      script.async = async;

      if (attributes) {
        for (const attribute of attributes) {
          if (attribute.name.toLowerCase() === 'csr') continue;
          if (attribute.value === null) continue;
          script.setAttribute(attribute.name, attribute.value);
        }
      }

      if (id) script.id = id;
      if (src) script.src = src;
      if (innerHtml) script.innerHTML = innerHtml;
      document.body.appendChild(script);
    };

    if (delay > 0) {
      const timeout = setTimeout(loadScript, delay);
      return () => clearTimeout(timeout);
    }

    loadScript();

    return () => {
      if (id) {
        const existing = document.getElementById(id);
        existing?.remove();
      }
    };
  }, [src, id, async, delay, attributes, innerHtml]);

  return null;
}
